import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import heroMethod from "@/assets/hero-method.jpg";
import { MarketingHeroImage } from "@/components/site/MarketingHeroImage";
import { EmphasizedTitle } from "@/components/site/EmphasizedTitle";
import { MethodStepDialog } from "@/components/method/MethodStepDialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  getEglyMethodIntro,
  getEglyMethodSteps,
  stepExplanation,
  type AudienceMode,
  type EglyMethodStep,
} from "@/lib/egly-method";
import { HERO_IMAGE_SIZES, heroPreloadLink } from "@/lib/marketing-images";
import { heroMethodPic } from "@/lib/marketing-images.hero-method";
import { getStep06DialogTeaser } from "@/lib/play-injury-free";
import { pageHead } from "@/lib/seo";
import { m } from "@/paraglide/messages.js";
import { ArrowRight } from "lucide-react";

const AUDIENCE_OPTIONS: AudienceMode[] = ["beginner", "amateur", "tournament"];

const AUDIENCE_LABELS: Record<AudienceMode, () => string> = {
  beginner: () => m.method_audience_beginner(),
  amateur: () => m.method_audience_amateur(),
  tournament: () => m.method_audience_tournament(),
};

export const Route = createFileRoute("/method")({
  head: () => {
    const head = pageHead({
      title: m.method_meta_title(),
      description: m.method_meta_description(),
      path: "/method",
      image: heroMethod,
    });
    return {
      ...head,
      links: [...(head.links ?? []), heroPreloadLink(heroMethodPic)],
    };
  },
  component: MethodPage,
});

function MethodPage() {
  const [audience, setAudience] = useState<AudienceMode>("amateur");
  const [selectedStep, setSelectedStep] = useState<EglyMethodStep | null>(null);
  const intro = getEglyMethodIntro();
  const steps = getEglyMethodSteps();
  const step06Teaser = getStep06DialogTeaser();

  return (
    <>
      <section className="relative isolate overflow-hidden bg-ink">
        <MarketingHeroImage
          picture={heroMethodPic}
          alt=""
          sizes={HERO_IMAGE_SIZES}
          priority
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/30" />
        <div className="container-page relative page-hero-pad">
          <span className="eyebrow">{m.nav_method()}</span>
          <h1 className="mt-4 max-w-3xl font-display text-hero leading-[0.95] md:text-8xl">
            <EmphasizedTitle
              lead={m.method_title_lead()}
              accent={m.method_title_accent()}
              stacked
            />
          </h1>
          <p className="mt-6 max-w-2xl text-foreground/80 md:text-lg">{m.method_subtitle()}</p>
        </div>
      </section>

      <section className="container-page page-follow-pad">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-foreground/75 md:text-lg">{intro[audience]}</p>

          <RadioGroup
            value={audience}
            onValueChange={(v) => setAudience(v as AudienceMode)}
            aria-label={m.method_audience_aria()}
            className="mt-8 inline-flex max-w-full flex-row flex-wrap items-center justify-center gap-0 rounded-none border border-white/15 bg-ink-soft p-1"
          >
            {AUDIENCE_OPTIONS.map((option) => (
              <label
                key={option}
                htmlFor={`audience-${option}`}
                className={`flex cursor-pointer items-center gap-2 px-3 py-2 font-display text-xs tracking-[0.12em] transition-colors sm:px-5 sm:py-2.5 sm:text-sm sm:tracking-[0.14em] ${
                  audience === option
                    ? "bg-ember-fill text-primary-foreground"
                    : "text-foreground/75 hover:text-foreground"
                }`}
              >
                <RadioGroupItem value={option} id={`audience-${option}`} className="sr-only" />
                {AUDIENCE_LABELS[option]()}
              </label>
            ))}
          </RadioGroup>
        </div>

        <div className="mt-14 grid gap-px bg-white/10 md:grid-cols-2">
          {steps.map((step) => (
            <button
              key={step.n}
              type="button"
              onClick={() => setSelectedStep(step)}
              aria-label={m.method_learn_more_about({ title: step.title })}
              className="group bg-ink p-10 text-start transition-colors hover:bg-ink-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ember focus-visible:ring-offset-2 focus-visible:ring-offset-ink"
            >
              <div className="flex items-start gap-5">
                <div className="font-display text-6xl leading-none text-ember">{step.n}</div>
                <div className="min-w-0 flex-1">
                  <h2 className="font-display text-2xl">{step.title}</h2>
                  <p className="mt-2 text-sm font-medium text-foreground/85">{step.summary}</p>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/75">
                    {stepExplanation(step, audience)}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 font-display text-xs tracking-[0.14em] text-ember transition-colors group-hover:text-ember-hot">
                    {m.method_learn_more()} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>

        <MethodStepDialog
          step={selectedStep}
          audience={audience}
          open={!!selectedStep}
          onClose={() => setSelectedStep(null)}
        />

        <div className="mt-16 border border-white/10 bg-black/40 p-8 text-center md:p-10">
          <p className="mx-auto max-w-2xl text-sm leading-relaxed text-foreground/75 md:text-base">
            {step06Teaser}
          </p>
          <Link
            to="/play-injury-free"
            className="mt-5 inline-flex items-center gap-2 font-display text-sm tracking-[0.14em] text-ember hover:text-foreground"
          >
            {m.method_protects_body()} <ArrowRight className="h-4 w-4 rtl:rotate-180" />
          </Link>
        </div>

        <div className="mt-16 flex flex-col items-center text-center">
          <h3 className="font-display text-4xl md:text-5xl">{m.method_ready()}</h3>
          <p className="mt-3 max-w-lg text-foreground/70">{m.method_ready_hint()}</p>
          <Link to="/schedule" className="btn-ember btn-ember-hover mt-8">
            {m.nav_book_lesson()} <ArrowRight className="h-5 w-5 rtl:rotate-180" />
          </Link>
        </div>
      </section>
    </>
  );
}
